import { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import moment from "moment";
import MUIDataTable from "mui-datatables";
import {
  Button,
  CircularProgress,
  Backdrop,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Box,
  Typography,
  Snackbar,
  Alert,
  Paper,
  Grid,
  Card,
  CardHeader,
  CardContent,
  Divider,
  Tab,
  Tabs,
  Avatar,
  Chip,
} from "@mui/material";
import {
  AccountBalanceWallet,
  MonetizationOn,
  AttachMoney,
  MoneyOff,
  Person,
  Business,
  Receipt,
  History,
} from "@mui/icons-material";
import CurrencyRupeeIcon from "@mui/icons-material/CurrencyRupee";
import { BASE_URL } from "../../../../components/config/config";
import BasicDateRangePicker from "../../../../components/common/datepicker";
import {
  canViewUserTransaction,
  canViewClientTransaction,
  canClientRecharge,
  canConsumerTechnicianRecharge,
} from "../../../../utility/permissionUtils";

const ManualWalletTransaction = () => {
  const globalState = useSelector((state) => state.AdminReducer);
  const accessToken = globalState?.userTokens?.accessToken;

  const userPermissions = globalState?.adminDetails?.role_id?.permissions;

  const hasManualWalletAccess =
    canViewUserTransaction(userPermissions) ||
    canViewClientTransaction(userPermissions) ||
    canClientRecharge(userPermissions) ||
    canConsumerTechnicianRecharge(userPermissions);

  // State management
  const [loader, setLoader] = useState(false);
  const [refundPenaltyRows, setRefundPenaltyRows] = useState([]);
  const [walletRechargeRows, setWalletRechargeRows] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [userType, setUserType] = useState("");
  const [userId, setUserId] = useState("");
  const [actionType, setActionType] = useState("");
  const [amount, setAmount] = useState("");
  const [ticketId, setTicketId] = useState("");
  const [bonus, setBonus] = useState("");
  const [remarks, setRemarks] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [filterUserType, setFilterUserType] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [openClientRechargeModal, setOpenClientRechargeModal] = useState(false);
  const [clientId, setClientId] = useState("");
  const [clientAmount, setClientAmount] = useState("");
  const [clientDescription, setClientDescription] = useState("");
  const [clientTransactions, setClientTransactions] = useState([]);
  const [clientSuccessMessage, setClientSuccessMessage] = useState("");
  const [showClientSuccessPopup, setShowClientSuccessPopup] = useState(false);
  const [clients, setClients] = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);
  const [allClientTransactions, setAllClientTransactions] = useState([]);
  const [filteredClientTransactions, setFilteredClientTransactions] = useState(
    []
  );
  const [clientFilter, setClientFilter] = useState("all");
  const [activeTab, setActiveTab] = useState(0);

  // Check permissions for rendering components
  const canShowUserTransactions = canViewUserTransaction(userPermissions);
  const canShowClientTransactions = canViewClientTransaction(userPermissions);
  const canShowClientRechargeButton = canClientRecharge(userPermissions);
  const canShowWalletActionButton =
    canConsumerTechnicianRecharge(userPermissions);

  // Filter available tabs based on permissions
  const getAvailableTabs = () => {
    const tabs = [];

    if (canShowUserTransactions) {
      tabs.push({ label: "User Transactions", index: 0 });
    }

    if (canShowClientTransactions) {
      tabs.push({ label: "Client Transactions", index: 1 });
    }

    return tabs;
  };

  const availableTabs = getAvailableTabs();

  // Adjust initial tab if current tab is not available
  useEffect(() => {
    const currentTabAvailable = availableTabs.some(
      (tab) => tab.index === activeTab
    );
    if (!currentTabAvailable && availableTabs.length > 0) {
      setActiveTab(availableTabs[0].index);
    }
  }, [userPermissions]);

  // Table columns
  const columns = [
    {
      name: "txnDate",
      label: "Transaction Date",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => moment(value).format("Do MMM, hh:mm A"),
      },
    },
    {
      name: "userType",
      label: "User Type",
      options: {
        customBodyRender: (value) => (
          <Chip
            label={value}
            color={value === "Technician" ? "primary" : "secondary"}
            size="small"
          />
        ),
      },
    },
    {
      name: "userId",
      label: "User ID",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => value || "N/A",
      },
    },
    {
      name: "ticketId",
      label: "Ticket ID",
      options: {
        filter: false,
        sort: false,
      },
    },
    {
      name: "actionType",
      label: "Category",
      options: {
        customBodyRender: (value) => (
          <Chip
            label={value}
            color={
              value === "Refund"
                ? "success"
                : value === "Penalty"
                ? "error"
                : value === "Wallet Added"
                ? "info"
                : "default"
            }
            size="small"
          />
        ),
      },
    },
    { name: "initiatedPaymentName", label: "Initiated By" },
    {
      name: "amount",
      label: "Amount",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <Typography
            fontWeight="bold"
            color={value >= 0 ? "success.main" : "error.main"}
          >
            ₹{Number(value).toFixed(2)}
          </Typography>
        ),
      },
    },
    {
      name: "bonus",
      label: "Bonus",
      options: {
        customBodyRender: (value) => (
          <Typography color="warning.main">
            ₹{Number(value || 0).toFixed(2)}
          </Typography>
        ),
      },
    },
    {
      name: "walletBalance",
      label: "Balance",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <Typography fontWeight="bold">
            ₹{Number(value || 0).toFixed(2)}
          </Typography>
        ),
      },
    },
    {
      name: "remarks",
      label: "Remarks",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (val) => val || "N/A",
      },
    },
  ];

  const clientColumns = [
    {
      name: "createdAt",
      label: "Date",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => moment(value).format("Do MMM, hh:mm A"),
      },
    },
    {
      name: "client_id",
      label: "Client ID",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "ticketId",
      label: "Request ID",
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: "amount",
      label: "Amount",
      options: {
        filter: false,
        sort: true,
        customBodyRender: (value) => (
          <Typography
            fontWeight="bold"
            color={value >= 0 ? "success.main" : "error.main"}
          >
            ₹{Number(value).toFixed(2)}
          </Typography>
        ),
      },
    },
    {
      name: "description",
      label: "Description",
      options: {
        filter: false,
        sort: false,
      },
    },
    {
      name: "transaction_type",
      label: "Type",
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value) => (
          <Chip
            label={value}
            color={value === "CREDITED" ? "success" : "error"}
            size="small"
          />
        ),
      },
    },
    {
      name: "performed_by",
      label: "Processed By",
      options: {
        filter: false,
        sort: false,
      },
    },
    {
      name: "remaining_balance",
      label: "New Balance",
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value) => (
          <Typography fontWeight="bold">
            ₹{Number(value || 0).toFixed(2)}
          </Typography>
        ),
      },
    },
  ];

  const options = {
    selectableRows: "none",
    responsive: "standard",
    filterType: "checkbox",
    print: false,
    download: false,
    viewColumns: false,
    elevation: 0,
  };

  // Data fetching functions
  const fetchTransactions = async () => {
    if (!canShowUserTransactions) return;

    setLoader(true);
    try {
      const categories = ["Refund", "Penalty", "BONUS", "Wallet Added"];
      const promises = categories.map((category) => {
        const params = new URLSearchParams();
        if (dateRange.from) params.append("fromDate", dateRange.from);
        if (dateRange.to) params.append("toDate", dateRange.to);
        if (filterUserType) params.append("userType", filterUserType);
        params.append("category", category);

        return axios.get(
          `${BASE_URL}admin/get-wallet-transactions-by-admin?${params}`,
          { headers: { Authorization: accessToken } }
        );
      });

      const results = await Promise.all(promises);
      const allData = results.flatMap((res) => res.data?.data || []);

      const formatted = allData.map((item) => ({
        txnDate: item.txnDate,
        userType: item.userType || "N/A",
        userId:
          item.userType === "Consumer"
            ? item.phoneNumber
            : item.technicianId || "N/A",
        ticketId: item.ticketId || "-",
        actionType: item.actionType || "N/A",
        initiatedPaymentName: item.initiatedPaymentName || "N/A",
        amount: item.amount || 0,
        bonus: item.bonus || 0,
        walletBalance: item.walletBalance
          ? Number(item.walletBalance).toFixed(2)
          : "0.00",
        remarks: item.remarks || item.description || "N/A",
      }));

      const rechargeData = formatted.filter((item) =>
        ["Wallet Added", "BONUS"].includes(item.actionType)
      );
      const refundPenaltyData = formatted.filter((item) =>
        ["Refund", "Penalty", "Ticket"].includes(item.actionType)
      );

      setWalletRechargeRows(rechargeData);
      setRefundPenaltyRows(refundPenaltyData);
    } catch (err) {
      console.error("Error fetching transactions:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleSubmit = async () => {
    if (!canConsumerTechnicianRecharge(userPermissions)) {
      alert("No permission to perform wallet actions.");
      return;
    }

    if (
      !userType ||
      !userId ||
      !amount ||
      !remarks ||
      ((actionType === "Refund" || actionType === "Penalty") && !ticketId)
    ) {
      alert("Please fill all required fields.");
      return;
    }

    try {
      setLoader(true);
      const payload = {
        adminId: globalState?.adminDetails?._id,
        userType,
        paymentCategory: actionType,
        balance: Number(amount),
        remarks,
        bonus: Number(bonus) || 0,
        ...(ticketId && { ticket_id: ticketId }),
        ...(userType === "Technician"
          ? { technician_id: userId }
          : {
              phone: { country_code: "+91", mobile_number: userId },
            }),
      };

      await axios.post(
        `${BASE_URL}admin/add_wallet_balance_by_admin`,
        payload,
        {
          headers: { Authorization: accessToken },
        }
      );

      const message =
        actionType === "Refund"
          ? "Refund initiated successfully."
          : actionType === "Penalty"
          ? "Penalty initiated successfully."
          : "Wallet recharge successful.";

      setSuccessMessage(message);
      setShowSuccessPopup(true);
      resetForm();
      setOpenModal(false);
      fetchTransactions();
    } catch (err) {
      console.error("Error submitting transaction:", err);
    } finally {
      setLoader(false);
    }
  };

  const fetchAllClients = async () => {
    if (!canShowClientTransactions && !canShowClientRechargeButton) return;

    try {
      setClientsLoading(true);
      const response = await axios.get(`${BASE_URL}admin/get-all-clients`, {
        headers: { Authorization: accessToken },
      });
      const clientsData = response.data?.data || [];
      setClients(clientsData);
      // Set first client as default if available
      if (clientsData.length > 0) {
        setClientFilter(clientsData[0].client_id);
        fetchClientTransactions(clientsData[0].client_id);
      }
    } catch (err) {
      console.error("Error fetching clients:", err);
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchClientTransactions = async (clientId) => {
    if (!canShowClientTransactions) return;

    try {
      setLoader(true);
      const params = new URLSearchParams();
      if (clientId) params.append("client_id", clientId);

      const response = await axios.get(
        `${BASE_URL}admin/admin-get-client-wallet-transactions?${params}`,
        { headers: { Authorization: accessToken } }
      );

      const sortedTransactions = (response.data?.data || []).sort(
        (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
      );

      // Map the data to transform ADDED to CREDITED
      const formattedTransactions = sortedTransactions.map((transaction) => ({
        ...transaction,
        transaction_type:
          transaction.transaction_type === "ADDED"
            ? "CREDITED"
            : transaction.transaction_type,

        ticketId: transaction.ticket_id || "-",
      }));

      setFilteredClientTransactions(formattedTransactions);
    } catch (err) {
      console.error("Error fetching client transactions:", err);
    } finally {
      setLoader(false);
    }
  };

  const handleClientRecharge = async () => {
    if (!canClientRecharge(userPermissions)) {
      alert("No permission to recharge client wallet.");
      return;
    }

    if (!clientId || !clientAmount || !clientDescription) {
      alert("Please fill all required fields.");
      return;
    }

    try {
      setLoader(true);
      const payload = {
        client_id: clientId,
        amount: Number(clientAmount),
        description: clientDescription,
      };

      await axios.post(`${BASE_URL}admin/admin-add-client-wallet`, payload, {
        headers: { Authorization: accessToken },
      });

      setClientSuccessMessage("Client wallet recharged successfully!");
      setShowClientSuccessPopup(true);
      setClientAmount("");
      setClientDescription("");

      if (clientFilter === "all") {
        fetchClientTransactions();
      } else {
        fetchClientTransactions(clientFilter);
      }

      setOpenClientRechargeModal(false);
    } catch (err) {
      console.error("Error recharging client wallet:", err);
    } finally {
      setLoader(false);
    }
  };

  const resetForm = () => {
    setUserType("");
    setUserId("");
    setActionType("");
    setAmount("");
    setTicketId("");
    setBonus("");
    setRemarks("");
  };

  const handleDateChange = (dates) => {
    setDateRange({
      from: dates[0] ? moment(dates[0]).format("YYYY-MM-DD") : "",
      to: dates[1] ? moment(dates[1]).format("YYYY-MM-DD") : "",
    });
  };

  useEffect(() => {
    if (hasManualWalletAccess) {
      fetchAllClients();
      fetchTransactions();
    }
  }, [dateRange, filterUserType, hasManualWalletAccess]);

  if (!hasManualWalletAccess) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          No permission to access manual wallet transactions.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <Backdrop
        open={loader}
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      <Box mb={3}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Wallet Management
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Manage wallet transactions, refunds, and client balances
            </Typography>
          </Box>
          <Grid
            item
            xs={12}
            md={4}
            sx={{ display: "flex", alignItems: "center" }}
          >
            {canShowClientRechargeButton && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<CurrencyRupeeIcon />}
                onClick={() => setOpenClientRechargeModal(true)}
              >
                Client Recharge
              </Button>
            )}
            {canShowWalletActionButton && (
              <Button
                variant="contained"
                color="secondary"
                style={{ marginLeft: "15px" }}
                startIcon={<AccountBalanceWallet />}
                onClick={() => setOpenModal(true)}
              >
                Wallet Action
              </Button>
            )}
          </Grid>
        </Box>
      </Box>

      <Card sx={{ mb: 3 }}>
        <CardHeader
          title="Filters"
          action={
            <BasicDateRangePicker
              onClickFilter={(val) => {
                setDateRange({
                  from: val[0] ? moment(val[0]).format("YYYY-MM-DD") : "",
                  to: val[1] ? moment(val[1]).format("YYYY-MM-DD") : "",
                });
              }}
              refreshData={() => {
                setDateRange({ from: "", to: "" });
              }}
            />
          }
        />
        <Divider />
        <CardContent>
          <Grid container spacing={2}>
            {canShowUserTransactions && (
              <Grid item xs={12} md={4} sx={{ minWidth: "200px" }}>
                <FormControl fullWidth>
                  <InputLabel>User Type</InputLabel>
                  <Select
                    value={filterUserType}
                    onChange={(e) => setFilterUserType(e.target.value)}
                    label="User Type"
                  >
                    <MenuItem value="">All Users</MenuItem>
                    <MenuItem value="Technician">Technician</MenuItem>
                    <MenuItem value="Consumer">Consumer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
            )}
            {canShowClientTransactions && (
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel>Filter Clients</InputLabel>
                  <Select
                    value={clientFilter}
                    onChange={(e) => {
                      setClientFilter(e.target.value);
                      if (e.target.value === "all") {
                        fetchClientTransactions();
                      } else {
                        fetchClientTransactions(e.target.value);
                      }
                    }}
                    label="Filter Clients"
                  >
                    <MenuItem value="all">All Clients</MenuItem>
                    {clients.map((client) => (
                      <MenuItem key={client._id} value={client.client_id}>
                        {client.client_id} - {client.client_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}
          </Grid>
        </CardContent>
      </Card>

      {availableTabs.length > 0 && (
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => setActiveTab(newValue)}
          sx={{ mb: 3 }}
        >
          <Tab label="User Transactions" icon={<Person />} />
          <Tab
            style={{ marginLeft: "20px" }}
            label="Client Transactions"
            icon={<Business />}
          />
        </Tabs>
      )}
      {activeTab === 0 && canShowUserTransactions && (
        <>
          <Card
            sx={{
              mb: 3,
              mx: "auto",
              overflowX: "auto",
              border: "1px solid #e0e0e0",
              borderRadius: 2,
            }}
          >
            <Divider />
            <CardContent>
              <MUIDataTable
                title={"Wallet Recharges"}
                data={walletRechargeRows}
                columns={columns}
                options={{
                  ...options,
                  selectableRows: "none",
                  responsive: "standard",
                  fixedHeader: false,
                }}
              />
            </CardContent>
          </Card>

          <Card>
            <Divider />
            <CardContent>
              <MUIDataTable
                title={"Refunds & Penalties"}
                data={refundPenaltyRows}
                columns={columns}
                options={{
                  ...options,
                  selectableRows: "none",
                  responsive: "standard",
                  fixedHeader: false,
                }}
              />
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === 1 && canShowClientTransactions && (
        <Card>
          <CardHeader
            title="Client Wallet Transactions"
            avatar={<Business color="primary" />}
            subheader={
              clientFilter !== "all"
                ? `Showing transactions for client: ${
                    clients.find((c) => c.client_id === clientFilter)
                      ?.client_name || clientFilter
                  }`
                : "Showing all client transactions"
            }
          />
          <Divider />
          <CardContent>
            <MUIDataTable
              title="Client Wallet Transactions"
              data={filteredClientTransactions}
              columns={clientColumns}
              options={{
                ...options,
                selectableRows: "none",
                responsive: "standard",
                fixedHeader: false,
              }}
            />
          </CardContent>
        </Card>
      )}

      {/* Wallet Action Dialog */}
      {canConsumerTechnicianRecharge(userPermissions) && (
        <Dialog
          open={openModal}
          onClose={() => setOpenModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            <AccountBalanceWallet color="primary" sx={{ mr: 1 }} />
            Wallet Transaction
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Action Type</InputLabel>
                  <Select
                    value={actionType}
                    onChange={(e) => setActionType(e.target.value)}
                    label="Action Type"
                    sx={{"minWidth" : 220}}
                  >
                    <MenuItem value="Wallet Added">Wallet Recharge</MenuItem>
                    <MenuItem value="Refund">Refund</MenuItem>
                    <MenuItem value="Penalty">Penalty</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>User Type</InputLabel>
                  <Select
                    value={userType}
                    onChange={(e) => setUserType(e.target.value)}
                    label="User Type"
                    sx={{"minWidth" : 220}}
                  >
                    <MenuItem value="Technician">Technician</MenuItem>
                    <MenuItem value="Consumer">Consumer</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                {userType === "Technician" ? (
                  <TextField
                    fullWidth
                    label="Technician ID"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  />
                ) : (
                  <TextField
                    fullWidth
                    label="Phone Number"
                    type="tel"
                    value={userId}
                    onChange={(e) => setUserId(e.target.value)}
                  />
                )}
              </Grid>

              {(actionType === "Refund" || actionType === "Penalty") && (
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Ticket ID"
                    value={ticketId}
                    onChange={(e) => setTicketId(e.target.value)}
                  />
                </Grid>
              )}

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Bonus"
                  type="number"
                  value={bonus}
                  onChange={(e) => setBonus(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Remarks"
                  multiline
                  rows={3}
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenModal(false)}>Cancel</Button>
            <Button
              onClick={handleSubmit}
              variant="contained"
              color="primary"
              disabled={
                !userType ||
                !userId ||
                !amount ||
                !remarks ||
                ((actionType === "Refund" || actionType === "Penalty") &&
                  !ticketId)
              }
            >
              Submit
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Client Recharge Dialog */}
      {canClientRecharge(userPermissions) && (
        <Dialog
          open={openClientRechargeModal}
          onClose={() => setOpenClientRechargeModal(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ display: "flex", alignItems: "center" }}>
            <MonetizationOn color="primary" sx={{ mr: 1 }} />
            Client Wallet Recharge
          </DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>Select Client</InputLabel>
                  <Select
                    value={clientId}
                    onChange={(e) => setClientId(e.target.value)}
                    label="Select Client"
                    disabled={clientsLoading}
                  >
                    {clientsLoading && (
                      <MenuItem disabled>
                        <CircularProgress size={24} />
                      </MenuItem>
                    )}
                    {clients.map((client) => (
                      <MenuItem key={client._id} value={client.client_id}>
                        {client.client_id} - {client.client_name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {clientId && (
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={{ p: 2, bgcolor: "background.default" }}
                  >
                    <Typography variant="subtitle2" gutterBottom>
                      Client Details:
                    </Typography>
                    {(() => {
                      const selectedClient = clients.find(
                        (c) => c.client_id === clientId
                      );
                      if (!selectedClient) return null;

                      return (
                        <Grid container spacing={1}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Name:</strong>{" "}
                              {selectedClient.client_name}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Contact:</strong>{" "}
                              {selectedClient.client_poc?.phone
                                ?.mobile_number || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Email:</strong>{" "}
                              {selectedClient.official_email || "N/A"}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2">
                              <strong>Address:</strong>{" "}
                              {selectedClient.address_details?.address_line ||
                                "N/A"}
                            </Typography>
                          </Grid>
                        </Grid>
                      );
                    })()}
                  </Paper>
                </Grid>
              )}

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Amount"
                  type="number"
                  value={clientAmount}
                  onChange={(e) => setClientAmount(e.target.value)}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={clientDescription}
                  onChange={(e) => setClientDescription(e.target.value)}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenClientRechargeModal(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleClientRecharge}
              variant="contained"
              color="primary"
              disabled={!clientId || !clientAmount || !clientDescription}
            >
              Recharge Wallet
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Success Notifications */}
      <Snackbar
        open={showSuccessPopup}
        autoHideDuration={3000}
        onClose={() => setShowSuccessPopup(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowSuccessPopup(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={showClientSuccessPopup}
        autoHideDuration={3000}
        onClose={() => setShowClientSuccessPopup(false)}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setShowClientSuccessPopup(false)}
          severity="success"
          sx={{ width: "100%" }}
        >
          {clientSuccessMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ManualWalletTransaction;
